import { Button, H3, IconArrowLeft } from '@wheely/ui-kit';
import cn from 'classnames';
import React, { ReactNode, useCallback, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import { ProgressLine } from '@src-common/view/components/ProgressLine';

import { BottomButtons } from '../BottomButtons';
import { CenteringContainer } from '../CenteringContainer';
import { ISwapTransitionRef, SwapTransition } from '../SwapTransition';

import { DelayedDisappearance } from './DelayedDisappearance';
import s from './styles.module.css';

export interface IContainerProps {
  onClickYes?: () => void;
  onClickNo?: () => void;
  onClickBack?: () => void;
  title?: ReactNode;
  subTitle?: ReactNode;
  step?: number;
  totalSteps?: number;
  transitionKey?: string;
  textNoButton?: string;
  textYesButton?: string;
  hideProgress?: boolean;
  hideHeader?: boolean;
  children?: ReactNode;
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const Dummy = React.forwardRef(props => props.children);

interface IHeaderProps extends JSX.IntrinsicAttributes {
  onClick: (event: any) => void;
  isBackDisabled: boolean;
  isProgressHidden: boolean;
  step: number;
  totalSteps: number;
}

const Header = ({ onClick, isBackDisabled, step, totalSteps, isProgressHidden }: IHeaderProps) => (
  <div className={s.top}>
    <button
      data-test-id="wizard__back-button"
      value="BACK"
      className={s.back}
      onClick={onClick}
      disabled={isBackDisabled}
    >
      <IconArrowLeft width={20} height={20} />
    </button>

    {!isProgressHidden && (
      <span className={s.steps} data-test-id="wizard__progress-text">
        <FormattedMessage
          defaultMessage="{step} of {totalSteps}"
          id="cE3DJO"
          description="Step progress label"
          values={{ step: step, totalSteps: totalSteps }}
        />
      </span>
    )}
    {!isProgressHidden && <ProgressLine className={s.progress} progress={step / totalSteps} />}
  </div>
);

const StepLayout = ({
  onClickYes,
  onClickBack,
  onClickNo,
  totalSteps = 1,
  step = 0,
  children,
  title,
  subTitle,
  textNoButton,
  textYesButton,
  transitionKey,
  hideProgress = false,
  hideHeader = false,
}: IContainerProps) => {
  const swapperRef = useRef<ISwapTransitionRef>(null);
  const handlePrevClickButton = useCallback(() => {
    swapperRef.current?.reverseNextTransition();
    onClickBack?.();
  }, [onClickBack]);

  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const Swap = transitionKey ? SwapTransition : Dummy;

  const innerHeaderRef = useRef<HTMLDivElement | null>(null);

  return (
    <>
      <CenteringContainer
        innerClassName={s.fixedArea}
        innerRef={innerHeaderRef}
        outerClassName={cn(s.fixedAreaWrapper, { [s.fixedAreaWrapperHidden]: hideHeader })}
      >
        <DelayedDisappearance<IHeaderProps>
          Component={Header}
          isHidden={hideHeader}
          props={{
            onClick: handlePrevClickButton,
            isBackDisabled: !onClickBack,
            isProgressHidden: hideProgress,
            step,
            totalSteps,
          }}
          triggerRef={innerHeaderRef}
        ></DelayedDisappearance>
      </CenteringContainer>

      <Swap ref={swapperRef} transitionKey={transitionKey ?? ''} targetElementRef={scrollAreaRef}>
        <CenteringContainer
          innerRef={scrollAreaRef}
          innerClassName={s.scrollArea}
          outerClassName={s.scrollAreaWrapper}
        >
          {(title || subTitle) && (
            <header className={s.header} data-test-id="wizard__step-title">
              <H3 className={s.title}>{title}</H3>
              {Boolean(subTitle) && (
                <p className={s.subTitle} role="doc-subtitle" data-test-id="wizard__step-sub-title">
                  {subTitle}
                </p>
              )}
            </header>
          )}
          {children}
          {(onClickYes || onClickNo) && (
            <BottomButtons>
              {onClickYes && (
                <Button value="YES" onClick={onClickYes}>
                  {textYesButton || (
                    <FormattedMessage
                      defaultMessage="Yes"
                      id="LeRDjJ"
                      description="Yes button text"
                    />
                  )}
                </Button>
              )}
              {onClickNo && (
                <Button value="NO" onClick={onClickNo} intent="none">
                  {textNoButton || (
                    <FormattedMessage
                      defaultMessage="No"
                      id="xlcvRJ"
                      description="No button text"
                    />
                  )}
                </Button>
              )}
            </BottomButtons>
          )}
        </CenteringContainer>
      </Swap>
    </>
  );
};

export { StepLayout };
